import { useState, useEffect } from 'react'
import {
  Users,
  Building2,
  Car,
  DoorOpen,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  TrendingUp,
  XCircle,
  BarChart3,
  Eye,
  UserPlus,
  LogIn,
  Mail,
  PlayCircle,
  X
} from 'lucide-react'
import {
  getDashboardStats,
  getRecentActivity,
  getAnalyticsSummary,
  getFirstTimeErrors,
  getActivityClipUrl,
  AnalyticsSummary,
  FirstTimeErrorsReport
} from '../lib/api'

interface DashboardStats {
  total_tenants: number
  active_tenants: number
  pending_tenants: number
  total_vehicles: number
  total_buildings: number
  total_units: number
  occupied_rooms: number
  vacant_rooms: number
  events_today: number
  events_this_week: number
  lpr_matches_today: number
  lpr_entry_today: number
  lpr_exit_today: number
  denied_events_today: number
}

interface Activity {
  type: string
  id: number
  title: string
  description: string
  timestamp: string | null
  status: string
  clip_available?: boolean
  clip_url?: string | null
  event_type?: string | null
  direction?: 'entry' | 'exit' | 'unknown' | null
  plate_matched?: boolean
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [activities, setActivities] = useState<Activity[]>([])
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null)
  const [firstTimeErrors, setFirstTimeErrors] = useState<FirstTimeErrorsReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      const [statsData, activityData, analyticsData, firstTimeErrorsData] = await Promise.all([
        getDashboardStats(),
        getRecentActivity(100),
        getAnalyticsSummary(30).catch(() => null),  // Don't fail dashboard if analytics is empty
        getFirstTimeErrors(30).catch(() => null)
      ])
      setStats(statsData as DashboardStats)
      setActivities((activityData as { activities: Activity[] }).activities || [])
      if (analyticsData) setAnalytics(analyticsData)
      if (firstTimeErrorsData) setFirstTimeErrors(firstTimeErrorsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }

  const refreshLiveData = async () => {
    try {
      const [statsData, activityData] = await Promise.all([
        getDashboardStats(),
        getRecentActivity(100),
      ])
      setStats(statsData as DashboardStats)
      setActivities((activityData as { activities: Activity[] }).activities || [])
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh dashboard')
    }
  }

  useEffect(() => {
    fetchData()
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') refreshLiveData()
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Unknown'
    return new Date(dateStr).toLocaleString()
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />
      case 'failed':
      case 'denied':
        return <XCircle className="w-4 h-4 text-red-500" />
      default:
        return <Clock className="w-4 h-4 text-gray-400" />
    }
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-600 mt-1">UniFi Access Manager Overview</p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Compact overview and live cameras */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_28rem] gap-5 mb-5 items-start">
        <div className="space-y-4 min-w-0">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard title="Active Residents" value={stats?.active_tenants || 0} subtitle={`${stats?.pending_tenants || 0} pending`} icon={<Users className="w-4 h-4 text-blue-500" />} color="blue" />
          <StatCard
            title="Total Vehicles"
            value={stats?.total_vehicles || 0}
            subtitle={`${stats?.lpr_matches_today || 0} LPR today`}
            rightStats={[
              { label: 'Entry', value: stats?.lpr_entry_today || 0 },
              { label: 'Exit', value: stats?.lpr_exit_today || 0 },
            ]}
            icon={<Car className="w-4 h-4 text-green-500" />}
            color="green"
          />
          <StatCard title="Buildings" value={stats?.total_buildings || 0} subtitle={`${stats?.total_units || 0} units`} icon={<Building2 className="w-4 h-4 text-purple-500" />} color="purple" />
          <StatCard title="Events Today" value={stats?.events_today || 0} subtitle={`${stats?.denied_events_today || 0} denied`} icon={<DoorOpen className="w-4 h-4 text-orange-500" />} color="orange" />
          <StatCard title="Occupancy" value={stats?.occupied_rooms || 0} subtitle={`${stats?.vacant_rooms || 0} vacant`} icon={<Building2 className="w-4 h-4 text-blue-500" />} color="blue" />
          <StatCard title="Weekly Activity" value={stats?.events_this_week || 0} subtitle="Access events" icon={<TrendingUp className="w-4 h-4 text-green-500" />} color="green" />
          <StatCard title="Security Alerts" value={stats?.denied_events_today || 0} subtitle="Denied today" icon={<AlertTriangle className="w-4 h-4 text-orange-500" />} color="orange" />
        </div>
        <RecentActivityCard activities={activities} formatDate={formatDate} getStatusIcon={getStatusIcon} entryToday={stats?.lpr_entry_today || 0} exitToday={stats?.lpr_exit_today || 0} />
        </div>

        <div className="space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm w-full">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Entry Tag</h2>
              <p className="text-xs text-gray-500">Live entrance camera</p>
            </div>
            <span className="inline-flex items-center gap-2 text-xs font-medium text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
              <span className="w-2 h-2 rounded-full bg-green-500" /> Live
            </span>
          </div>
          <div className="bg-black aspect-video overflow-hidden">
            <iframe src="/hls/entrance/?controls=false&muted=true&autoplay=true&playsinline=true" title="Entry Tag live camera" className="w-full h-full border-0 pointer-events-none" allow="autoplay" />
          </div>
          <div className="p-2.5 bg-white">
            <button type="button" className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700" aria-label="Open Gate preview button">
              <DoorOpen className="w-4 h-4" /> Open Gate
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm w-full">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Exit Tag</h2>
              <p className="text-xs text-gray-500">Live LPR camera</p>
            </div>
            <span className="inline-flex items-center gap-2 text-xs font-medium text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
              <span className="w-2 h-2 rounded-full bg-green-500" /> Live
            </span>
          </div>
          <div className="bg-black aspect-video overflow-hidden">
            <iframe src="/hls/exit_tag/?controls=false&muted=true&autoplay=true&playsinline=true" title="Exit Tag live LPR camera" className="w-full h-full border-0 pointer-events-none" allow="autoplay" />
          </div>
        </div>
        </div>
      </div>

      {/* Site Analytics & Registration Funnel */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Site Traffic Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary-500" />
                <h3 className="text-lg font-semibold text-gray-900">Site Traffic</h3>
              </div>
              <span className="text-xs text-gray-400">Last 30 days</span>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-5">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{analytics.total_page_views.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Page Views</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{analytics.unique_ips.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Unique Visitors</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{analytics.unique_sessions.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Sessions</p>
              </div>
            </div>

            {/* Today's Stats */}
            <div className="bg-blue-50 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-800">Today</span>
                <div className="flex gap-4">
                  <span className="text-sm text-blue-700">
                    <Eye className="w-3.5 h-3.5 inline mr-1" />
                    {analytics.today.page_views} views
                  </span>
                  <span className="text-sm text-blue-700">
                    <Users className="w-3.5 h-3.5 inline mr-1" />
                    {analytics.today.unique_visitors} visitors
                  </span>
                </div>
              </div>
            </div>

            {/* Top Referrers */}
            {analytics.top_referrers.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Top Referrers</h4>
                <div className="space-y-1.5">
                  {analytics.top_referrers.slice(0, 5).map((ref, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 truncate max-w-[200px]" title={ref.referrer}>
                        {ref.referrer.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                      </span>
                      <span className="text-gray-400 font-mono text-xs">{ref.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Registration Funnel Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-green-500" />
                <h3 className="text-lg font-semibold text-gray-900">Registration Funnel</h3>
              </div>
              <span className="text-xs text-gray-400">Last 30 days</span>
            </div>

            <div className="space-y-3">
              {/* Login Page Views */}
              <FunnelRow
                icon={<Eye className="w-4 h-4 text-gray-500" />}
                label="Login Page Views"
                value={analytics.funnel.login_page_views}
                max={analytics.funnel.login_page_views}
                color="bg-gray-400"
              />

              {/* Self Registration */}
              <FunnelRow
                icon={<UserPlus className="w-4 h-4 text-purple-500" />}
                label="Self-Registration Started"
                value={analytics.funnel.self_registration_started}
                max={analytics.funnel.login_page_views}
                color="bg-purple-400"
              />
              <FunnelRow
                icon={<CheckCircle className="w-4 h-4 text-purple-600" />}
                label="Self-Registration Completed"
                value={analytics.funnel.self_registration_completed}
                max={analytics.funnel.login_page_views}
                color="bg-purple-600"
              />

              <div className="border-t border-gray-100 my-2" />

              {/* First Time Setup */}
              <FunnelRow
                icon={<LogIn className="w-4 h-4 text-blue-500" />}
                label="First-Time Setup Started"
                value={analytics.funnel.first_time_setup_started}
                max={analytics.funnel.login_page_views}
                color="bg-blue-400"
              />
              <FunnelRow
                icon={<CheckCircle className="w-4 h-4 text-blue-600" />}
                label="First-Time Setup Completed"
                value={analytics.funnel.first_time_setup_completed}
                max={analytics.funnel.login_page_views}
                color="bg-blue-600"
              />

              <div className="border-t border-gray-100 my-2" />

              {/* Returning Logins */}
              <FunnelRow
                icon={<LogIn className="w-4 h-4 text-green-500" />}
                label="Returning Login Started"
                value={analytics.funnel.returning_login_started}
                max={analytics.funnel.login_page_views}
                color="bg-green-400"
              />
              <FunnelRow
                icon={<CheckCircle className="w-4 h-4 text-green-600" />}
                label="Returning Login Completed"
                value={analytics.funnel.returning_login_completed}
                max={analytics.funnel.login_page_views}
                color="bg-green-600"
              />
            </div>

            {/* Conversion Rate */}
            {analytics.funnel.login_page_views > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Registration Conversion</span>
                  <span className="font-semibold text-green-600">
                    {((analytics.funnel.self_registration_completed / analytics.funnel.login_page_views) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-gray-500">Overall Login Success</span>
                  <span className="font-semibold text-blue-600">
                    {(((analytics.funnel.returning_login_completed + analytics.funnel.first_time_setup_completed) / analytics.funnel.login_page_views) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* First-Time Setup Errors & Follow-up */}
      {firstTimeErrors && (
        <div className="bg-white rounded-lg border border-gray-200 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">First-Time Setup Issues</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Residents who hit setup errors and may need outreach
              </p>
            </div>
            <div className="text-xs text-gray-500">
              Last {firstTimeErrors.period_days} days
            </div>
          </div>

          <div className="px-6 py-4 border-b border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricChip label="Started" value={firstTimeErrors.summary.first_time_started} />
            <MetricChip label="Completed" value={firstTimeErrors.summary.first_time_completed} />
            <MetricChip label="Failed" value={firstTimeErrors.summary.first_time_failed} />
            <MetricChip label="Users Impacted" value={firstTimeErrors.summary.unique_failed_users} />
          </div>

          {firstTimeErrors.users.length === 0 ? (
            <div className="px-6 py-6 text-sm text-gray-500">No failed first-time setup users in this period.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Failures</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Last Error</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {firstTimeErrors.users.slice(0, 50).map((u) => (
                    <tr key={u.email} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-900">{u.email}</td>
                      <td className="px-6 py-3 text-sm text-gray-700">{u.failure_count}</td>
                      <td className="px-6 py-3 text-sm text-gray-700">{u.last_error_reason || 'unknown'}</td>
                      <td className="px-6 py-3">
                        {u.completed_after_failure ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Completed
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            Needs follow-up
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <a
                          href={`mailto:${u.email}?subject=${encodeURIComponent('Need help finishing your OvrWatch account setup')}&body=${encodeURIComponent('Hi,\n\nWe noticed you may have had trouble completing first-time account setup. Reply to this email and we will help you finish registration.\n\nThanks,\nProperty Management')}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-primary-600 text-white hover:bg-primary-700"
                        >
                          <Mail className="w-3.5 h-3.5" />
                          Email Resident
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  )
}

interface StatCardProps {
  title: string
  value: number
  subtitle: string
  icon: React.ReactNode
  color: 'blue' | 'green' | 'purple' | 'orange'
  rightStats?: Array<{ label: string; value: number }>
}

function RecentActivityCard({
  activities,
  formatDate,
  getStatusIcon,
  entryToday,
  exitToday,
}: {
  activities: Activity[]
  formatDate: (date: string | null) => string
  getStatusIcon: (status: string) => React.ReactNode
  entryToday: number
  exitToday: number
}) {
  const [activityFilter, setActivityFilter] = useState<'all' | 'entry' | 'exit' | 'matched'>('all')
  const [clipEventId, setClipEventId] = useState<number | null>(null)
  const [clipUrl, setClipUrl] = useState<string | null>(null)
  const [clipActivity, setClipActivity] = useState<Activity | null>(null)
  const [clipLoading, setClipLoading] = useState<number | null>(null)
  const filteredActivities = activities.filter((activity) => {
    if (activityFilter === 'entry') return activity.direction === 'entry'
    if (activityFilter === 'exit') return activity.direction === 'exit'
    if (activityFilter === 'matched') return activity.plate_matched === true
    return true
  })

  const closeClip = () => {
    setClipUrl(null)
    setClipEventId(null)
    setClipActivity(null)
  }

  const playClip = async (activity: Activity) => {
    closeClip()
    setClipLoading(activity.id)
    try {
      const url = await getActivityClipUrl(activity.id)
      setClipUrl(url)
      setClipEventId(activity.id)
      setClipActivity(activity)
    } finally {
      setClipLoading(null)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-white">
        <h2 className="text-base font-semibold text-gray-900">Recent Activity</h2>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {([
            ['all', 'All'],
            ['entry', `Entry ${entryToday}`],
            ['exit', `Exit ${exitToday}`],
            ['matched', 'Plate Match'],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setActivityFilter(value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${activityFilter === value ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {filteredActivities.length === 0 ? (
        <div className="p-5 text-center text-sm text-gray-500">No recent activity</div>
      ) : (
        <div className="divide-y divide-gray-100 max-h-[430px] overflow-y-auto overscroll-contain">
          {filteredActivities.map((activity) => (
            <div key={`${activity.type}-${activity.id}`} className="px-4 py-3 hover:bg-gray-50">
              <div className="flex items-start gap-3">
                {activity.type === 'access_event' ? <DoorOpen className="w-4 h-4 mt-0.5 text-primary-500" /> : <Users className="w-4 h-4 mt-0.5 text-green-500" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-900 truncate">{activity.title}</h3>
                    {getStatusIcon(activity.status)}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{activity.description}</p>
                </div>
                {activity.clip_available && (
                  <button
                    type="button"
                    onClick={() => playClip(activity)}
                    disabled={clipLoading === activity.id}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-50"
                  >
                    <PlayCircle className="h-4 w-4" /> {clipLoading === activity.id ? 'Loading' : 'Clip'}
                  </button>
                )}
                <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(activity.timestamp)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {clipEventId !== null && clipUrl && clipActivity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={closeClip}>
          <div className="w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="min-w-0">
                <h3 className="truncate text-sm font-semibold text-gray-900">{clipActivity.title}</h3>
                <p className="truncate text-xs text-gray-500">{clipActivity.description}</p>
              </div>
              <button type="button" onClick={closeClip} className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100" aria-label="Close clip">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="aspect-video bg-black">
              <video key={clipUrl} src={clipUrl} controls autoPlay playsInline preload="metadata" className="h-full w-full object-contain" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function StatCard({ title, value, subtitle, icon, color, rightStats }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    purple: 'bg-purple-50 border-purple-200',
    orange: 'bg-orange-50 border-orange-200',
  }

  return (
    <div className={`rounded-lg border p-3.5 min-h-[106px] ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-xs font-medium text-gray-600">{title}</h3>
        {icon}
      </div>
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        </div>
        {rightStats && (
          <div className="flex flex-shrink-0 items-end gap-2 text-right">
            {rightStats.map((stat) => (
              <div key={stat.label} className="min-w-[3.25rem]">
                <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
                <p className="text-lg font-bold leading-5 text-gray-900">{stat.value.toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface FunnelRowProps {
  icon: React.ReactNode
  label: string
  value: number
  max: number
  color: string
}

function FunnelRow({ icon, label, value, max, color }: FunnelRowProps) {
  const pct = max > 0 ? (value / max) * 100 : 0
  return (
    <div className="flex items-center gap-3">
      {icon}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-sm text-gray-700">{label}</span>
          <span className="text-sm font-semibold text-gray-900">{value.toLocaleString()}</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${color} transition-all`}
            style={{ width: `${Math.max(pct, value > 0 ? 2 : 0)}%` }}
          />
        </div>
      </div>
    </div>
  )
}

function MetricChip({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-semibold text-gray-900">{value.toLocaleString()}</p>
    </div>
  )
}
